"use client";

import {
  CombinedSearchResult,
  VenueSearchResult,
  findMatchingAll,
} from "@/data/search";
import {
  DiscoverEntrySource,
  SearchEvents,
  SearchSurface,
  trackEvent,
} from "@/analytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFeatureGate } from "statsig-react";

import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import {
  MapPinIcon,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  PlayCircle,
  Store,
  X,
  Search,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { CTA_SOURCES } from "@punchup/analytics";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { cn } from "@/utils/cn";
import { useUserComedianFollows } from "@/hooks/follows";
import { useQuickSubscribeExperiment } from "@/hooks/useQuickSubscribeExperiment";
import { QuickSubscribeButton } from "./QuickSubscribeButton";
import { PunchupVerifiedBadge } from "@punchup/ui-common/components/common";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { VenueLogo } from "@punchup/venues/client";
import { PunchupIcon } from "@punchup/ui-common/components/common";
import { SpecialRow } from "@/data/specials";
import { debounce } from "@/components/common/utils";
import { getCDNImageURL } from "@/data/images";
import { formatRuntime } from "@punchup/utils";
import { useSearchOverlay } from "./SearchOverlayContext";
import { ShowWithComedian } from "@/data/shows";
import { getApiClient } from "@punchup/utils/apiRegistry";
import dayjs from "@punchup/utils/dayjs";
import { useDiscoverData } from "@/hooks/useDiscoverData";

const INITIAL_RESULTS_PER_SECTION = 5;

export function SearchOverlay() {
  const { gate } = useFeatureGate("search_discover_redesign");
  const { isOpen, close } = useSearchOverlay();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<CombinedSearchResult>({
    comedians: [],
    specials: [],
    venues: [],
  });
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  // Default state data from shared hook
  const {
    allComediansMap,
    rankedComedians,
    trendingSpecials,
    loaded: defaultStateLoaded,
  } = useDiscoverData({ comedianLimit: 12, specialLimit: 15 });

  // Upcoming shows for searched comedians
  const [upcomingShows, setUpcomingShows] = useState<
    Map<string, ShowWithComedian[]>
  >(new Map());

  // Track whether a search has completed (to avoid showing "no results" while typing)
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  // Trending comedians from the shared hook (already ranked by momentum/ALS)
  const trendingComedians = rankedComedians.slice(0, 7);

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearchTerm("");
      setSearchResults({ comedians: [], specials: [], venues: [] });
      setExpandedSections({});
      setUpcomingShows(new Map());
      setHasSearchCompleted(false);
    }
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Fetch upcoming shows for matched comedians
  const fetchUpcomingShowsForComedians = useCallback(
    async (comedianIds: string[]) => {
      if (comedianIds.length === 0) return;
      try {
        const showPromises = comedianIds.slice(0, 3).map(async (comedianId) => {
          try {
            const shows = await getApiClient().get<ShowWithComedian[]>(
              `/api/shows?comedianId=${comedianId}&startDatetime=${dayjs().utc().toISOString()}`
            );
            return { comedianId, shows: shows.slice(0, 3) };
          } catch {
            return { comedianId, shows: [] };
          }
        });

        const results = await Promise.all(showPromises);
        const showMap = new Map<string, ShowWithComedian[]>();
        results.forEach(({ comedianId, shows }) => {
          if (shows.length > 0) {
            showMap.set(comedianId, shows);
          }
        });
        setUpcomingShows(showMap);
      } catch (error) {
        console.error("Error fetching upcoming shows:", error);
      }
    },
    []
  );

  // Debounced search — no shimmer, results update in-place
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setSearchResults({ comedians: [], specials: [], venues: [] });
        setHasSearchCompleted(false);
        return;
      }
      try {
        const supabase = createClientComponentClient<Database>();
        const results = await findMatchingAll(supabase, query);
        setSearchResults(results);
        setHasSearchCompleted(true);
        setExpandedSections({});

        // Fetch upcoming shows for matched comedians
        if (results.comedians.length > 0) {
          fetchUpcomingShowsForComedians(results.comedians.map((c) => c.id));
        } else {
          setUpcomingShows(new Map());
        }

        if (query.length > 2) {
          const eventName =
            results.comedians.length === 0 &&
            results.specials.length === 0 &&
            results.venues.length === 0
              ? SearchEvents.SEARCH_RESULTS_RETURNED_EMPTY
              : SearchEvents.SEARCH_RESULTS_RETURNED;
          trackEvent(eventName, {
            searchTerm: query,
            numComedians: results.comedians.length,
            numSpecials: results.specials.length,
            numVenues: results.venues.length,
          });
        }
      } catch (error) {
        console.error("Error searching:", error);
      }
    }, 400),
    [fetchUpcomingShowsForComedians]
  );

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults({ comedians: [], specials: [], venues: [] });
      setUpcomingShows(new Map());
      setHasSearchCompleted(false);
      return;
    }
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const handleNavigate = (href: string) => {
    close();
    router.push(href);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newState = { ...prev, [section]: !prev[section] };
      trackEvent(SearchEvents.CLICKED_SEE_ALL_SECTION, {
        section,
        expanded: newState[section],
        searchTerm,
      });
      return newState;
    });
  };

  if (!gate.value || !isOpen) return null;

  const hasSearched = searchTerm.trim().length > 0;
  const totalResults =
    searchResults.comedians.length +
    searchResults.specials.length +
    searchResults.venues.length;

  return (
    // Below the login-as banner, which stays visible and clickable.
    <div className="fixed inset-x-0 bottom-0 top-app-banner z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-white md:bg-black/40 md:backdrop-blur-sm"
        onClick={close}
      />

      {/* Overlay container */}
      <div className="absolute inset-0 md:inset-auto md:top-[10vh] md:left-1/2 md:-translate-x-1/2 md:w-[min(720px,90vw)] md:max-h-[75vh] md:rounded-2xl bg-white md:shadow-2xl flex flex-col overflow-hidden">
        {/* Search header */}
        <div className="flex items-center gap-4 px-5 md:px-7 py-4 md:py-5 border-b border-gray-100">
          <Search className="h-6 w-6 md:h-7 md:w-7 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search comedians, venues, content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 text-base md:text-lg bg-transparent outline-none placeholder:text-gray-400 text-gray-900"
          />
          {searchTerm.length > 0 ? (
            <button
              onClick={() => {
                setSearchTerm("");
                inputRef.current?.focus();
              }}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Clear search"
            >
              <X className="h-5 w-5 md:h-6 md:w-6 text-gray-500" />
            </button>
          ) : (
            <button
              onClick={close}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors md:hidden"
              aria-label="Close search"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-hide">
          {/* Show results if we have them */}
          {totalResults > 0 && (
            <SearchResults
              searchTerm={searchTerm}
              searchResults={searchResults}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              onNavigate={handleNavigate}
              allComediansMap={allComediansMap}
              upcomingShows={upcomingShows}
            />
          )}

          {/* Show "no results" only after a search has completed with 0 results */}
          {hasSearched && hasSearchCompleted && totalResults === 0 && (
            <div className="px-5 md:px-7 pt-8 pb-2 text-center">
              <p className="text-base md:text-lg text-gray-500">
                No results for &quot;{searchTerm}&quot;
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Try a different search term
              </p>
            </div>
          )}

          {/* Show default/trending state when no search or no results */}
          {(totalResults === 0 || !hasSearched) && (
            <DefaultState
              trendingComedians={trendingComedians}
              trendingSpecials={trendingSpecials}
              loaded={defaultStateLoaded}
              onNavigate={handleNavigate}
              allComediansMap={allComediansMap}
            />
          )}
        </div>

        {/* Footer hint - desktop only */}
        <div className="hidden md:flex items-center justify-between px-6 py-2.5 border-t border-gray-100 bg-gray-50 text-sm text-gray-400">
          <span>
            Press{" "}
            <kbd className="px-1 py-0.5 rounded bg-white border border-gray-200 text-gray-500">
              /
            </kbd>{" "}
            to search
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-white border border-gray-200 text-gray-500">
              esc
            </kbd>{" "}
            to close
          </span>
        </div>
      </div>
    </div>
  );
}

// --- Default State ---

function DefaultState({
  trendingComedians,
  trendingSpecials,
  loaded,
  onNavigate,
  allComediansMap,
}: {
  trendingComedians: Comedian[];
  trendingSpecials: SpecialRow[];
  loaded: boolean;
  onNavigate: (href: string) => void;
  allComediansMap: Map<string, Comedian>;
}) {
  // Read here (not in SearchOverlay) so the experiment exposure only logs for
  // users who actually see the trending default state, not on every page load
  // of the globally-mounted overlay.
  const showQuickSubscribe = useQuickSubscribeExperiment();

  if (!loaded) {
    return <div className="p-4 md:p-6" />;
  }

  // trendingComedians is already personalized (ALS) or momentum-based
  const horizontalComedians = trendingComedians;

  return (
    <div className="py-4 md:py-6">
      {/* Horizontal avatar scroll — Substack-style */}
      {horizontalComedians.length > 0 && (
        <div className="px-5 md:px-7 mb-5">
          <div className="flex gap-4 md:gap-5 overflow-x-auto scrollbar-hide pt-1 pb-2">
            {horizontalComedians.map((comedian) => (
              <TrendingComedianAvatar
                key={comedian.id}
                comedian={comedian}
                onNavigate={onNavigate}
                showSubscribe={showQuickSubscribe}
              />
            ))}
            {/* See all button at end of scroll */}
            <button
              onClick={() => {
                trackEvent(SearchEvents.CLICKED_SEE_ALL_ON_SEARCH_CAROUSEL, {
                  comedianCount: horizontalComedians.length,
                });
                onNavigate(
                  `/discover?source=${DiscoverEntrySource.SEARCH_CAROUSEL}`
                );
              }}
              className="flex flex-col items-center justify-center gap-1.5 flex-shrink-0 w-[72px] md:w-[88px]"
            >
              <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-gray-100 flex items-center justify-center">
                <ChevronRight className="h-6 w-6 text-gray-400" />
              </div>
              <span className="text-xs md:text-sm text-primary font-semibold text-center">
                See all
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Trending content rows */}
      {trendingSpecials.length > 0 && (
        <div className="px-5 md:px-7">
          <h3 className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-400 mb-3">
            Trending
          </h3>
          {trendingSpecials.map((special) => {
            const comedian = allComediansMap.get(special.comedian_id);
            const comedianSlug = comedian?.slug ?? special.comedian_id;
            const imgId =
              special.preview_img ||
              special.small_preview_img ||
              special.poster_img_with_titles;
            const thumbUrl = imgId ? getCDNImageURL(imgId) : null;
            return (
              <button
                key={special.id}
                onClick={() => {
                  trackEvent(SearchEvents.CLICKED_SPECIAL_FROM_SEARCH, {
                    specialId: special.id,
                    comedianId: special.comedian_id,
                    searchTerm: "",
                    source: SearchSurface.SEARCH_OVERLAY,
                  });
                  onNavigate(`/${comedianSlug}/${special.slug}`);
                }}
                className="flex items-center gap-4 py-3 md:py-4 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors w-full text-left group"
              >
                <div className="flex items-center gap-3 flex-shrink-0">
                  <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-gray-400 flex-shrink-0" />
                  {thumbUrl && (
                    <div className="relative h-11 w-[60px] md:h-12 md:w-[68px] flex-shrink-0 rounded-md overflow-hidden bg-gray-100">
                      <Image
                        src={thumbUrl}
                        alt={special.title ?? ""}
                        width={68}
                        height={48}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base md:text-lg font-medium text-gray-900 truncate">
                    {special.title}
                  </p>
                  {comedian?.display_name && (
                    <p className="text-sm text-gray-500 truncate">
                      {comedian.display_name}
                    </p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrendingComedianAvatar({
  comedian,
  onNavigate,
  showSubscribe,
}: {
  comedian: Comedian;
  onNavigate: (href: string) => void;
  showSubscribe?: boolean;
}) {
  const imageUrl = comedian.about_img
    ? getCDNImageURL(comedian.about_img)
    : null;
  const { isFollowing: followsComedian } = useUserComedianFollows();
  const isFollowing = followsComedian(comedian.id);

  return (
    <div className="flex flex-col items-center gap-1.5 flex-shrink-0 w-[72px] md:w-[88px]">
      <button
        onClick={() => {
          trackEvent(SearchEvents.CLICKED_COMEDIAN_FROM_SEARCH, {
            comedianId: comedian.id,
            searchTerm: "",
            source: SearchSurface.SEARCH_OVERLAY,
          });
          onNavigate(`/${comedian.slug}`);
        }}
        className="relative"
      >
        <div
          className={cn(
            "h-16 w-16 md:h-20 md:w-20 rounded-full overflow-hidden bg-gray-100",
            isFollowing && "ring-2 ring-primary ring-offset-1"
          )}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={comedian.display_name ?? ""}
              width={80}
              height={80}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <PunchupIcon className="h-7 w-7 opacity-40" />
            </div>
          )}
        </div>
      </button>
      <span className="text-xs md:text-sm text-gray-700 text-center truncate w-full leading-tight">
        {comedian.display_name}
      </span>
      {showSubscribe && (
        <QuickSubscribeButton
          comedian={comedian}
          followLocation={FOLLOW_LOCATIONS.SEARCH}
          ctaSource={CTA_SOURCES.SEARCH_TRENDING_CAROUSEL_SUBSCRIBE}
          onNavigate={onNavigate}
          variant="compact"
        />
      )}
    </div>
  );
}

// --- Search Results ---

function SearchResults({
  searchTerm,
  searchResults,
  expandedSections,
  toggleSection,
  onNavigate,
  allComediansMap,
  upcomingShows,
}: {
  searchTerm: string;
  searchResults: CombinedSearchResult;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
  onNavigate: (href: string) => void;
  allComediansMap: Map<string, Comedian>;
  upcomingShows: Map<string, ShowWithComedian[]>;
}) {
  return (
    <div className="py-2">
      {searchResults.comedians.length > 0 && (
        <ResultSection
          title="Comedians"
          count={searchResults.comedians.length}
          expanded={expandedSections["comedians"]}
          onToggle={() => toggleSection("comedians")}
          showToggle={
            searchResults.comedians.length > INITIAL_RESULTS_PER_SECTION
          }
        >
          {searchResults.comedians
            .slice(
              0,
              expandedSections["comedians"]
                ? undefined
                : INITIAL_RESULTS_PER_SECTION
            )
            .map((comedian) => (
              <div key={comedian.id}>
                <ComedianResultRow
                  comedian={comedian}
                  searchTerm={searchTerm}
                  onNavigate={onNavigate}
                />
                {/* Upcoming shows for this comedian */}
                {upcomingShows.has(comedian.id) && (
                  <div className="ml-14 md:ml-[72px] mb-2">
                    {upcomingShows.get(comedian.id)!.map((show) => (
                      <NearbyShowRow
                        key={show.id}
                        show={show}
                        comedian={comedian}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
        </ResultSection>
      )}

      {searchResults.venues.length > 0 && (
        <ResultSection
          title="Venues"
          count={searchResults.venues.length}
          expanded={expandedSections["venues"]}
          onToggle={() => toggleSection("venues")}
          showToggle={searchResults.venues.length > INITIAL_RESULTS_PER_SECTION}
        >
          {searchResults.venues
            .slice(
              0,
              expandedSections["venues"]
                ? undefined
                : INITIAL_RESULTS_PER_SECTION
            )
            .map((venue) => (
              <VenueResultRow
                key={venue.id}
                venue={venue}
                searchTerm={searchTerm}
                onNavigate={onNavigate}
              />
            ))}
        </ResultSection>
      )}

      {searchResults.specials.length > 0 && (
        <ResultSection
          title="Content"
          count={searchResults.specials.length}
          expanded={expandedSections["content"]}
          onToggle={() => toggleSection("content")}
          showToggle={
            searchResults.specials.length > INITIAL_RESULTS_PER_SECTION
          }
        >
          {searchResults.specials
            .slice(
              0,
              expandedSections["content"]
                ? undefined
                : INITIAL_RESULTS_PER_SECTION
            )
            .map((special) => (
              <SpecialResultRow
                key={special.id}
                special={special}
                searchTerm={searchTerm}
                onNavigate={onNavigate}
                allComediansMap={allComediansMap}
              />
            ))}
        </ResultSection>
      )}
    </div>
  );
}

// --- Shared sub-components ---

function ResultSection({
  title,
  count,
  children,
  expanded,
  onToggle,
  showToggle,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
  expanded?: boolean;
  onToggle: () => void;
  showToggle: boolean;
}) {
  return (
    <div className="px-5 md:px-7 py-2 md:py-3">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-400">
          {title}
          <span className="ml-1.5">({count})</span>
        </h3>
      </div>
      <div className="divide-y divide-gray-50">{children}</div>
      {showToggle && (
        <button
          onClick={onToggle}
          className="flex items-center justify-center gap-1 w-full mt-2 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {expanded ? "Show less" : `See all ${count}`}
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  );
}

function ComedianResultRow({
  comedian,
  searchTerm,
  onNavigate,
}: {
  comedian: Comedian;
  searchTerm: string;
  onNavigate: (href: string) => void;
}) {
  const imageUrl = comedian.about_img
    ? getCDNImageURL(comedian.about_img)
    : null;

  return (
    <div className="flex items-center gap-3 md:gap-4 py-3 md:py-4 px-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors">
      <button
        onClick={() => {
          trackEvent(SearchEvents.CLICKED_COMEDIAN_FROM_SEARCH, {
            comedianId: comedian.id,
            searchTerm,
            source: SearchSurface.SEARCH_OVERLAY,
          });
          onNavigate(`/${comedian.slug}`);
        }}
        className="flex items-center gap-3 md:gap-4 flex-1 min-w-0 text-left"
      >
        <div className="relative h-11 w-11 md:h-14 md:w-14 flex-shrink-0 rounded-full overflow-hidden bg-gray-100">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={comedian.display_name ?? ""}
              width={56}
              height={56}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <PunchupIcon className="h-5 w-5 md:h-6 md:w-6 opacity-40" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base md:text-lg font-medium text-gray-900 truncate">
            {comedian.display_name}
          </p>
          {comedian.about && (
            <p className="text-sm text-gray-500 truncate">{comedian.about}</p>
          )}
        </div>
      </button>
      <QuickSubscribeButton
        comedian={comedian}
        followLocation={FOLLOW_LOCATIONS.SEARCH}
        ctaSource={CTA_SOURCES.SEARCH_RESULTS_ROW_SUBSCRIBE}
        onNavigate={onNavigate}
        variant="row"
      />
    </div>
  );
}

function NearbyShowRow({
  show,
  comedian,
  onNavigate,
}: {
  show: ShowWithComedian;
  comedian: Comedian;
  onNavigate: (href: string) => void;
}) {
  const showDate = dayjs(show.datetime);
  const venue = show.venue || show.location || "";

  return (
    <button
      onClick={() => {
        onNavigate(`/${comedian.slug}/tickets`);
      }}
      className="flex items-center gap-2.5 py-2 px-2 -mx-2 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
    >
      <Calendar className="h-4 w-4 md:h-[18px] md:w-[18px] text-primary flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm md:text-base text-gray-700">
          {showDate.format("ddd, MMM D")}
        </p>
        {venue && <p className="text-xs text-gray-400 truncate">{venue}</p>}
      </div>
      <span className="text-xs md:text-sm text-primary font-medium flex-shrink-0 ml-auto">
        Tickets
      </span>
    </button>
  );
}

function VenueResultRow({
  venue,
  searchTerm,
  onNavigate,
}: {
  venue: VenueSearchResult;
  searchTerm: string;
  onNavigate: (href: string) => void;
}) {
  return (
    <button
      onClick={() => {
        trackEvent(SearchEvents.CLICKED_VENUE_FROM_SEARCH, {
          venueId: venue.id,
          venueName: venue.name,
          searchTerm,
          source: SearchSurface.SEARCH_OVERLAY,
        });
        onNavigate(`/v/${venue.slug}`);
      }}
      className="flex items-center gap-3 md:gap-4 py-3 md:py-4 px-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors w-full text-left group"
    >
      <div className="relative h-11 w-11 md:h-14 md:w-14 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
        <VenueLogo
          venueSlug={venue.slug}
          logoImg={venue.logo_img}
          venueName={venue.name}
          imageWidth={56}
          imageHeight={56}
          className="w-full h-full p-0.5"
          imageClassName="object-contain"
          placeholderContent={<Store className="h-5 w-5 text-gray-300" />}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-base md:text-lg font-medium text-gray-900 truncate">
            {venue.name}
          </p>
          {venue.has_punchup_ticketing && (
            <PunchupVerifiedBadge
              className="!w-4 !h-4 flex-shrink-0"
              withTooltip
            />
          )}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <MapPinIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{venue.city_state}</span>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
    </button>
  );
}

function SpecialResultRow({
  special,
  searchTerm,
  onNavigate,
  allComediansMap,
}: {
  special: SpecialRow;
  searchTerm: string;
  onNavigate: (href: string) => void;
  allComediansMap: Map<string, Comedian>;
}) {
  const imageId =
    special.preview_img ||
    special.small_preview_img ||
    special.poster_img_with_titles;
  const thumbnailUrl = imageId ? getCDNImageURL(imageId) : null;

  const comedian = allComediansMap.get(special.comedian_id);
  const comedianSlug = comedian?.slug ?? special.comedian_id;
  const runtimeDisplay =
    special.runtime && special.runtime >= 1
      ? formatRuntime(special.runtime)
      : null;

  return (
    <button
      onClick={() => {
        trackEvent(SearchEvents.CLICKED_SPECIAL_FROM_SEARCH, {
          specialId: special.id,
          comedianId: special.comedian_id,
          searchTerm,
          source: SearchSurface.SEARCH_OVERLAY,
        });
        onNavigate(`/${comedianSlug}/${special.slug}`);
      }}
      className="flex items-center gap-3 md:gap-4 py-3 md:py-4 px-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors w-full text-left group"
    >
      <div className="relative h-11 w-[60px] md:h-14 md:w-[78px] flex-shrink-0 rounded-md overflow-hidden bg-gray-100">
        {thumbnailUrl ? (
          <Image
            src={thumbnailUrl}
            alt={special.title ?? ""}
            width={78}
            height={56}
            className="object-cover w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PlayCircle className="h-5 w-5 text-gray-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base md:text-lg font-medium text-gray-900 truncate">
          {special.title}
        </p>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          {comedian?.display_name && (
            <span className="truncate">{comedian.display_name}</span>
          )}
          {comedian?.display_name && runtimeDisplay && (
            <span className="flex-shrink-0">&middot;</span>
          )}
          {runtimeDisplay && (
            <span className="flex-shrink-0">{runtimeDisplay}</span>
          )}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
    </button>
  );
}
